import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ativarAppCheck, erroDeVerificacao, MENSAGEM_VERIFICACAO } from "../utils/appCheck";
import { db } from "../firebase";
import { doc, getDoc, getDocs, collection, query, where, limit, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { resumoOrcamentoPublico, moedaPublica } from "../utils/resumoOrcamentoPublico";
import { ItemOrcamentoPublico } from "./commerce/ItemOrcamentoPublico";
import "./orcamentoPublico.css";
import { Lightbox } from "./ui/Lightbox";

const LOGO_PADRAO = "Print Control";

// Página pública (cliente final SEM Firebase Auth) — registrar o pedido de
// verdade em users/{uid}/pedidos precisa passar por Cloud Function (Admin
// SDK), as regras do Firestore não deixam esse client escrever ali.
const fns = getFunctions(undefined, "us-central1");
const cfRegistrarPedido = httpsCallable(fns, "registrarPedidoOrcamentoPublico");
const mensagemRegistro = e => (erroDeVerificacao(e) ? MENSAGEM_VERIFICACAO : e?.message || "Não foi possível enviar o pedido. Confira sua conexão e tente novamente.");

// getDoc com tentativas — celular costuma falhar a 1ª leitura por rede instável
async function getDocComRetry(ref, tentativas = 3) {
  let ultimoErro;
  for (let i = 0; i < tentativas; i++) {
    try { return await getDoc(ref); }
    catch (e) { ultimoErro = e; await new Promise(r => setTimeout(r, 600 * (i + 1))); }
  }
  throw ultimoErro;
}

// Doc antigo (de antes do orçamento virar carrinho, ou antes da aprovação
// virar por item) tem formatos diferentes — normaliza tudo pro formato
// atual: {itens:[], aprovados:{índice: bool}}. Sem isso, link já mandado
// antes dessas mudanças quebrava a tela.
function normalizarPublico(d) {
  let base = d;
  if (!Array.isArray(d.itens)) {
    const { nome, fotos, preco, altura, observacoes, cores, visualizacao3d, ...resto } = d;
    base = {
      ...resto,
      nomeCliente: d.nomeCliente || "",
      itens: [{ nome, fotos: fotos || [], preco: preco || 0, quantidade: 1, altura, observacoes, cores: cores || [], visualizacao3d: visualizacao3d || null }],
      total: preco || 0,
    };
  }
  if (!base.aprovados) base = { ...base, aprovados: base.aprovado ? Object.fromEntries(base.itens.map((_, i) => [i, true])) : {} };
  if (!base.pedidosRegistrados) base = { ...base, pedidosRegistrados: {} };
  return base;
}

export function OrcamentoPublico() {
  const { vendedorId, orcamentoId, slug } = useParams();
  // App Check no link público de orçamento (registrarPedidoOrcamentoPublico)
  useEffect(() => { ativarAppCheck(fns.app); }, []);
  const [loja, setLoja] = useState(null);
  const [orc, setOrc] = useState(null);
  const [uidReal, setUidReal] = useState(null);
  const [idReal, setIdReal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [aprovandoIdx, setAprovandoIdx] = useState(null);
  const [registrando, setRegistrando] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [revisando, setRevisando] = useState(false);
  const [enviadoAgora, setEnviadoAgora] = useState(false);
  const resumoRef = useRef(null);
  const ocupadoRef = useRef(false);
  const revisaoRef = useRef("");
  const [lightbox, setLightbox] = useState(null); // { fotos, idx }

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setLoading(true); setErro(""); setOrc(null); setUidReal(null); setIdReal(null); setFeedback(""); setRevisando(false); setEnviadoAgora(false);
      try {
        let uid = vendedorId, orcId = orcamentoId;

        // Link novo: /orcamento/:slug — resolve pelo mapeamento global
        if (slug) {
          const slugSnap = await getDocComRetry(doc(db, "orcamento_slugs", slug));
          if (!slugSnap.exists()) { setErro("Orçamento não encontrado — o link pode estar errado ou ter sido apagado."); setLoading(false); return; }
          uid = slugSnap.data().uid;
          orcId = slugSnap.data().orcamentoId;
        }
        if (cancelado) return;

        // Resolve a loja pelo slug ou pelo uid — mesmo padrão de /loja/:vendedorId
        let lojaSnap = await getDocComRetry(doc(db, "lojas", uid));
        if (!lojaSnap.exists() || !lojaSnap.data().uid) {
          const q = query(collection(db, "lojas"), where("uid", "==", uid), limit(1));
          const res = await getDocs(q);
          if (!res.empty) lojaSnap = res.docs[0];
        }
        const uidFinal = lojaSnap.exists() ? lojaSnap.data().uid : uid;
        if (cancelado) return;
        setUidReal(uidFinal);
        setIdReal(orcId);
        if (lojaSnap.exists()) setLoja(lojaSnap.data());

        const orcRef = doc(db, `users/${uidFinal}/orcamentos_publicos`, orcId);
        const orcSnap = await getDocComRetry(orcRef);
        if (cancelado) return;
        if (!orcSnap.exists()) { setErro("Orçamento não encontrado — o link pode ter sido apagado."); setLoading(false); return; }

        const dados = normalizarPublico(orcSnap.data());
        const expiraEm = dados.expira_em?.toDate ? dados.expira_em.toDate() : null;
        if (expiraEm && expiraEm < new Date()) { setErro("Este link expirou. Peça um orçamento novo pro vendedor."); setLoading(false); return; }

        setOrc(dados);
        setLoading(false);

        // Marca visualização — não bloqueia a tela se falhar
        const patch = { ultima_visualizacao_em: serverTimestamp() };
        if (!dados.primeira_visualizacao_em) patch.primeira_visualizacao_em = serverTimestamp();
        updateDoc(orcRef, patch).catch(() => {});
      } catch (e) {
        if (!cancelado) { setErro("Não foi possível carregar o orçamento. Verifique sua conexão."); setLoading(false); }
      }
    })();
    return () => { cancelado = true; };
  }, [vendedorId, orcamentoId, slug]);


  // Mantém aprovações e itens enviados sincronizados quando o link abre em outra tela.
  useEffect(() => {
    if (!uidReal || !idReal) return;
    return onSnapshot(doc(db, 'users/' + uidReal + '/orcamentos_publicos', idReal), snap => {
      if (snap.exists()) setOrc(normalizarPublico(snap.data()));
      else setErro("Este orçamento não está mais disponível. Entre em contato com o vendedor.");
    }, () => {});
  }, [uidReal, idReal]);

  const itens = orc?.itens || [];
  const resumo = resumoOrcamentoPublico(orc);
  const expiraEm = orc?.expira_em?.toDate?.();
  const expirado = !!expiraEm && expiraEm < new Date();
  const bloqueado = registrando || aprovandoIdx !== null;
  const assinatura = JSON.stringify(resumo.pendentes.map(l => [l.index, itens[l.index]?.nome, l.quantidade, l.unitario]));
  const revisaoMudou = revisando && revisaoRef.current !== assinatura;

  const aprovarItem = async (index, aprovado) => {
    if (!orc || !uidReal || !idReal || ocupadoRef.current || expirado) return;
    const indices = index === "todos" ? resumo.linhas.filter(l => !l.enviado && !l.aprovado && l.valido).map(l => l.index) : [index];
    if (!indices.length || indices.some(i => orc.pedidosRegistrados?.[i] || !resumo.linhas[i]?.valido)) return;
    ocupadoRef.current = true; setAprovandoIdx(index); setFeedback(""); setRevisando(false); setEnviadoAgora(false);
    try {
      const patch = {aprovado_em:serverTimestamp()};
      indices.forEach(i => { patch['aprovados.' + i] = aprovado; });
      await updateDoc(doc(db, 'users/' + uidReal + '/orcamentos_publicos', idReal), patch);
      setOrc(o => ({...o,aprovados:{...o.aprovados,...Object.fromEntries(indices.map(i => [i,aprovado]))}}));
    } catch { setFeedback("Não foi possível salvar a aprovação. Confira sua conexão e tente novamente."); }
    finally { ocupadoRef.current = false; setAprovandoIdx(null); }
  };

  const revisar = () => {
    if (bloqueado || expirado || !resumo.valido || !resumo.pendentes.length) return;
    revisaoRef.current = assinatura; setRevisando(true); setFeedback("");
    resumoRef.current?.scrollIntoView({behavior:"smooth",block:"start"});
    resumoRef.current?.focus({preventScroll:true});
  };

  const fecharPedido = async () => {
    if (!orc || !uidReal || !idReal || ocupadoRef.current || !revisando || revisaoMudou || expirado || !resumo.valido || !resumo.pendentes.length) return;
    ocupadoRef.current = true; setRegistrando(true); setFeedback("");
    const indices = resumo.pendentes.map(l => l.index);
    try {
      await cfRegistrarPedido({uid:uidReal,orcamentoId:idReal,indices,totalEsperadoCentavos:resumo.aprovado});
      setOrc(o => ({...o,pedidosRegistrados:{...o.pedidosRegistrados,...Object.fromEntries(indices.map(i => [i,true]))}}));
      setRevisando(false); setEnviadoAgora(true);
    } catch (e) {
      setFeedback(mensagemRegistro(e));
      setRevisando(false);
      try { const snap = await getDoc(doc(db, 'users/' + uidReal + '/orcamentos_publicos', idReal)); if (snap.exists()) setOrc(normalizarPublico(snap.data())); } catch {}
    } finally { ocupadoRef.current = false; setRegistrando(false); }
  };

  const nomeLoja = loja?.empresa || loja?.nome || LOGO_PADRAO;
  const todosEnviados = itens.length > 0 && resumo.linhas.every(l => l.enviado);
  const podeRevisar = resumo.pendentes.length > 0 && resumo.valido && !bloqueado && !expirado;

  return <main className="public-quote-page">
    <header className="public-quote-header"><div className="public-quote-brand">{loja?.logoUrl ? <img src={loja.logoUrl} alt="" /> : <span className="public-quote-brand-symbol" aria-hidden="true">◇</span>}<div><strong>{nomeLoja}</strong><span>Impressão 3D feita para você</span></div></div><span className="public-quote-header-label">ORÇAMENTO DIGITAL</span></header>
    {loading && <div className="public-quote-state" role="status"><span className="public-quote-kicker">SÓ UM INSTANTE</span><h1>Preparando seu orçamento…</h1><p>Estamos carregando os produtos e os valores.</p></div>}
    {!loading && erro && <div className="public-quote-state" role="alert"><h1>Orçamento indisponível</h1><p>{erro}</p><button className="public-quote-button" onClick={() => window.location.reload()}>Tentar novamente</button></div>}
    {!loading && !erro && orc && <>
      <section className="public-quote-intro"><div><span className="public-quote-kicker">PREPARADO PARA {orc.nomeCliente || "VOCÊ"}</span><h1>Suas ideias,<br/><em>prontas para ganhar forma.</em></h1><p>Confira cada detalhe, aprove os itens que deseja e revise o pedido antes de enviar.</p></div><div className="public-quote-meta"><span>{itens.length} {itens.length === 1 ? "produto no orçamento" : "produtos no orçamento"}</span>{expiraEm && <strong>Válido até {expiraEm.toLocaleDateString("pt-BR")}</strong>}</div></section>
      <ol className="public-quote-steps" aria-label="Etapas do pedido"><li className={!revisando && !todosEnviados ? "is-current" : ""}><span>1</span>Escolha os itens</li><li className={revisando ? "is-current" : ""}><span>2</span>Revise o pedido</li><li className={todosEnviados ? "is-current" : ""}><span>3</span>Envie ao vendedor</li></ol>
      <div className="public-quote-layout"><section className="public-quote-products" aria-label="Produtos do orçamento"><div className="public-quote-section-heading"><h2>Feito para você</h2>{resumo.linhas.some(l => !l.enviado && !l.aprovado) && <button type="button" className="public-quote-text-button" disabled={bloqueado || expirado || !resumo.valido} onClick={() => aprovarItem("todos",true)}>{aprovandoIdx === "todos" ? "Salvando…" : "Aprovar todos"}</button>}</div>
        {!itens.length && <p className="public-quote-notice">Este orçamento ainda não tem produtos. Entre em contato com o vendedor.</p>}
        {resumo.linhas.map(linha => <ItemOrcamentoPublico key={linha.index} item={itens[linha.index]} linha={linha} bloqueado={bloqueado || expirado} salvando={aprovandoIdx === linha.index || aprovandoIdx === "todos"} onZoom={(fotos,idx) => setLightbox({fotos,idx})} onAprovar={aprovarItem} />)}
      </section>
      <aside className="public-quote-summary" ref={resumoRef} tabIndex={-1} aria-label="Resumo do pedido"><span className="public-quote-kicker">TUDO À VISTA</span><h2>{revisando ? "Confirme seu pedido" : "Resumo do orçamento"}</h2>
        {resumo.pendentes.length ? <ul className="public-quote-summary-items">{resumo.pendentes.map(l => <li key={l.index}><div><strong>{itens[l.index].nome || "Item do orçamento"}</strong><span>{l.quantidade} × {moedaPublica(l.unitario)}</span></div><b>{moedaPublica(l.subtotal)}</b></li>)}</ul> : <p className="public-quote-summary-empty">{todosEnviados ? "Todos os itens já foram enviados." : "Aprove os itens que deseja. Eles aparecerão aqui para você conferir."}</p>}
        <dl className="public-quote-totals"><div><dt>Orçamento completo</dt><dd>{resumo.valido ? moedaPublica(resumo.total) : "A conferir"}</dd></div>{resumo.linhas.some(l => l.enviado) && <div><dt>Itens já enviados</dt><dd>{moedaPublica(resumo.enviado)}</dd></div>}<div className="public-quote-total-selected"><dt>{todosEnviados ? "Total enviado" : "Total a enviar"}<small>{todosEnviados ? "Itens confirmados no pedido" : `${resumo.pendentes.length} ${resumo.pendentes.length === 1 ? "item aprovado" : "itens aprovados"}`}</small></dt><dd>{moedaPublica(todosEnviados ? resumo.enviado : resumo.aprovado)}</dd></div></dl>
        {(enviadoAgora || todosEnviados) && <div className="public-quote-success" role="status"><strong>✓ Pedido enviado!</strong><p>O vendedor recebeu os itens. Combine com ele o pagamento e os próximos passos.</p></div>}
        {feedback && <div className="public-quote-error" role="alert">{feedback}</div>}
        {!resumo.valido && <p className="public-quote-error" role="alert">Há um valor ou quantidade a conferir. Peça ao vendedor para revisar o orçamento.</p>}
        {expirado && <p className="public-quote-error" role="alert">Este orçamento expirou. Peça ao vendedor um novo link.</p>}
        {revisaoMudou && <p className="public-quote-notice" role="status">Os itens mudaram. Confira o resumo atualizado antes de continuar.</p>}
        {!todosEnviados && (revisando && !revisaoMudou ? <div className="public-quote-review"><p>Ao confirmar, os itens acima serão enviados ao vendedor. Esta etapa não realiza pagamento.</p><button type="button" className="public-quote-button" disabled={!podeRevisar} onClick={fecharPedido}>{registrando ? "Enviando pedido…" : "Confirmar e enviar pedido"}</button><button type="button" className="public-quote-text-button" disabled={bloqueado} onClick={() => setRevisando(false)}>Voltar e ajustar itens</button></div> : <><button type="button" className="public-quote-button" onClick={revisar} disabled={!podeRevisar}>{revisaoMudou ? "Revisar alterações" : "Revisar pedido →"}</button><p className="public-quote-help">Você confere tudo antes de enviar.</p></>)}
      </aside></div>
      {!todosEnviados && !revisando && <div className="public-quote-mobile-bar"><div><span>Total aprovado</span><strong>{moedaPublica(resumo.aprovado)}</strong></div><button type="button" className="public-quote-button" disabled={!podeRevisar} onClick={revisar}>Revisar pedido →</button></div>}
    </>}
    <footer className="public-quote-footer">Orçamento preparado por <strong>{nomeLoja}</strong><span>Organizado com Print Control</span></footer>
    <Lightbox fotos={lightbox?.fotos || []} indiceInicial={lightbox?.idx || 0} aberto={!!lightbox} onFechar={() => setLightbox(null)} />
  </main>;
}
